import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Loader2, PackageSearch, Plus, ShoppingCart, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDebounce } from '@/components/hooks/use-debounce'
import {
  useAddWorkOrderPart,
  useCatalogParts,
  useDeleteWorkOrderPart,
  usePurchaseOrders,
  useWorkOrderParts,
} from '@/features/parts/hooks'
import { POETA, POTracking } from '@/features/parts/po-tracking'
import { CreatePODialog, POStatusBadge } from '@/features/parts/purchase-orders'
import { formatMoney } from '@/lib/format'
import type { PurchaseOrder } from '@/types/parts'

// The line's latest non-canceled PO (PO lines snapshot work order part line
// ids at creation) drives the fulfillment chip in the parts table.
function latestOrderForLine(lineId: string, orders: PurchaseOrder[]): PurchaseOrder | null {
  let latest: PurchaseOrder | null = null
  for (const po of orders) {
    if (po.status === 'canceled') continue
    if (!po.lines.some((l) => l.work_order_part_id === lineId)) continue
    if (!latest || new Date(po.created_at) > new Date(latest.created_at)) latest = po
  }
  return latest
}

/**
 * PartsCard is the parts line editor on the work order detail page. Lines
 * picked from the price book default to the catalog's sale price; the work
 * order's aggregate parts_cost is recomputed server-side on every change.
 */
export function PartsCard({ workOrderId }: { workOrderId: string }) {
  const { data: lines = [], isLoading } = useWorkOrderParts(workOrderId)
  const { data: orders = [] } = usePurchaseOrders({ work_order_id: workOrderId })
  const addMutation = useAddWorkOrderPart(workOrderId)
  const deleteMutation = useDeleteWorkOrderPart(workOrderId)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [poOpen, setPoOpen] = useState(false)
  const [deletingLine, setDeletingLine] = useState<{ id: string; description: string } | null>(null)

  const total = useMemo(() => lines.reduce((sum, l) => sum + l.line_total, 0), [lines])

  return (
    <Card className="md:col-span-2">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <PackageSearch className="h-4 w-4" /> Parts
          </CardTitle>
          <CardDescription>
            Lines from the price book. The parts cost above is the sum of these lines; order and
            track them via purchase orders below.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPoOpen(true)}
            disabled={lines.length === 0}
          >
            <ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> New PO
          </Button>
          <Button size="sm" onClick={() => setPickerOpen(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Part
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : lines.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No parts yet — add lines from the price book or enter a custom line.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Part number</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="w-20 text-right">Qty</TableHead>
                <TableHead className="w-28 text-right">Unit</TableHead>
                <TableHead className="w-28 text-right">Total</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => {
                const order = latestOrderForLine(line.id, orders)
                return (
                  <TableRow key={line.id}>
                    <TableCell className="font-mono text-xs">{line.part_number || '—'}</TableCell>
                    <TableCell>{line.description}</TableCell>
                    <TableCell>
                      {order ? (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1.5">
                            <POStatusBadge status={order.status} />
                            <Link
                              to="/service/purchase-orders/$poId"
                              params={{ poId: order.id }}
                              className="font-mono text-xs hover:underline"
                            >
                              {order.po_number}
                            </Link>
                          </div>
                          <POETA po={order} className="text-xs" />
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not ordered</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{line.quantity}</TableCell>
                    <TableCell className="text-right">{formatMoney(line.unit_cost)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMoney(line.line_total)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        aria-label="Remove part line"
                        disabled={deleteMutation.isPending}
                        onClick={() =>
                          setDeletingLine({ id: line.id, description: line.description })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
              <TableRow>
                <TableCell colSpan={5} className="text-right text-sm text-muted-foreground">
                  Total
                </TableCell>
                <TableCell className="text-right font-semibold">{formatMoney(total)}</TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        )}

        {orders.length > 0 && (
          <div className="mt-6">
            <p className="mb-2 text-sm font-medium">Purchase orders</p>
            <div className="flex flex-col gap-2">
              {orders.map((po) => (
                <div
                  key={po.id}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border px-3 py-2 text-sm"
                >
                  <Link
                    to="/service/purchase-orders/$poId"
                    params={{ poId: po.id }}
                    className="font-mono text-xs font-medium hover:underline"
                  >
                    {po.po_number}
                  </Link>
                  <span className="text-xs text-muted-foreground">{po.supplier_name}</span>
                  <POStatusBadge status={po.status} />
                  <span className="text-xs text-muted-foreground">
                    ETA <POETA po={po} className="text-xs" />
                  </span>
                  <POTracking po={po} />
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatMoney(po.total)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <CatalogPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={(part) => {
          addMutation.mutate(
            { catalog_part_id: part.id },
            { onSuccess: () => setPickerOpen(false) },
          )
        }}
        onAddCustom={(partNumber, description, unitCost) => {
          addMutation.mutate(
            { part_number: partNumber || undefined, description, unit_cost: unitCost, quantity: 1 },
            { onSuccess: () => setPickerOpen(false) },
          )
        }}
        isPending={addMutation.isPending}
        addError={addMutation.error instanceof Error ? addMutation.error.message : null}
      />

      <CreatePODialog open={poOpen} onOpenChange={setPoOpen} presetWorkOrderId={workOrderId} />

      {/* Part-line delete confirmation: removing a line recomputes the work
          order's parts cost, so make the consequence explicit. */}
      <Dialog open={!!deletingLine} onOpenChange={(o) => !o && setDeletingLine(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove part line?</DialogTitle>
            <DialogDescription>
              “{deletingLine?.description}” is removed from this work order and its parts cost is
              recalculated. Purchase-order snapshots that already reference it are unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingLine(null)}>
              Keep Line
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (!deletingLine) return
                deleteMutation.mutate(deletingLine.id, { onSettled: () => setDeletingLine(null) })
              }}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove Line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

// --- Catalog picker ---

interface CatalogPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (part: { id: string; part_number: string; name: string }) => void
  onAddCustom: (partNumber: string, description: string, unitCost: number) => void
  isPending: boolean
  addError: string | null
}

function CatalogPickerDialog({
  open,
  onOpenChange,
  onPick,
  onAddCustom,
  isPending,
  addError,
}: CatalogPickerDialogProps) {
  const [search, setSearch] = useState('')
  const [customMode, setCustomMode] = useState(false)
  const [customNumber, setCustomNumber] = useState('')
  const [customDesc, setCustomDesc] = useState('')
  const [customCost, setCustomCost] = useState('')
  const debounced = useDebounce(search, 300)

  const { data, isLoading } = useCatalogParts(
    open && !customMode ? { q: debounced || undefined, limit: 20 } : undefined,
  )

  const reset = () => {
    setSearch('')
    setCustomMode(false)
    setCustomNumber('')
    setCustomDesc('')
    setCustomCost('')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add part</DialogTitle>
          <DialogDescription>
            Search the price book, or add a custom line with your own cost.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            placeholder="Search part number or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={customMode}
          />
          <Button variant="outline" onClick={() => setCustomMode(!customMode)}>
            {customMode ? 'Search catalog' : 'Custom line'}
          </Button>
        </div>

        {customMode ? (
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="custom-part-number">Part number</Label>
              <Input
                id="custom-part-number"
                value={customNumber}
                onChange={(e) => setCustomNumber(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="custom-desc">Description</Label>
              <Input
                id="custom-desc"
                value={customDesc}
                onChange={(e) => setCustomDesc(e.target.value)}
                placeholder="e.g. Bottom paint, quart"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="custom-cost">Unit cost ($)</Label>
              <Input
                id="custom-cost"
                type="number"
                min="0"
                step="0.01"
                value={customCost}
                onChange={(e) => setCustomCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <DialogFooter>
              <Button
                disabled={!customDesc || isPending}
                onClick={() => onAddCustom(customNumber, customDesc, Number(customCost) || 0)}
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add line
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto rounded-md border">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !data || data.parts.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No catalog parts match. Use “Custom line” to add one manually.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Part number</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="w-16" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.parts.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono text-xs">{p.part_number}</TableCell>
                      <TableCell className="max-w-64">
                        <span className="block truncate">{p.name}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {(p.sale_price ?? p.list_price)
                          ? formatMoney((p.sale_price ?? p.list_price) as number)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isPending}
                          onClick={() => onPick(p)}
                        >
                          Add
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
        {addError && <p className="text-sm text-destructive">{addError}</p>}
      </DialogContent>
    </Dialog>
  )
}
